import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { totalSymbols, totalCategories, formatCount } from "@/data/stats";
import {
  totalFontStyles,
  totalKaomoji,
  totalKaomojiMoods,
  totalLennyFaces,
  totalDecorativeCombos,
  totalDecorativeCollections,
} from "@/data/content-stats";

const TITLE = "About — the copy-paste symbol, text-faces & generator hub";
const DESC =
  "CoolSymbols.cool is a fast, free, ad-free hub for copy-paste Unicode symbols, kaomoji and Lenny faces, fancy-text generators, decorative combos and SVG/PNG export.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: abs("/about") },
    ],
    links: [{ rel: "canonical", href: abs("/about") }],
  }),
  component: About,
});

function About() {
  const symbols = formatCount(totalSymbols);
  const cats = formatCount(totalCategories);
  const fonts = formatCount(totalFontStyles);
  const kaomoji = formatCount(totalKaomoji);
  const kaomojiMoods = formatCount(totalKaomojiMoods);
  const lenny = formatCount(totalLennyFaces);
  const decoCombos = formatCount(totalDecorativeCombos);
  const decoCollections = formatCount(totalDecorativeCollections);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 sm:px-6">
        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          More than a <span className="text-gradient">symbol library.</span>
        </h1>

        <div className="prose prose-invert mt-8 max-w-none space-y-5 text-foreground/90">
          <p>
            CoolSymbols.cool is a fast, free place to copy and paste — and create — text
            symbols. It started as a better version of the old "copy a symbol" sites and grew
            into a full hub: <strong>{symbols} Unicode symbols</strong> across{" "}
            <Link to="/categories" className="text-accent hover:underline">{cats} categories</Link>,
            plus a set of tools for turning plain text into something more expressive. No ads,
            no popups, no signup — click anything to copy it instantly.
          </p>

          <h2 className="font-display text-xl font-semibold">What you'll find</h2>
          <ul className="ml-6 list-disc space-y-2 text-foreground/85">
            <li>
              <strong>
                <Link to="/categories" className="text-accent hover:underline">Symbols by category</Link>
              </strong>{" "}
              — hearts, stars, arrows, math, currency, animals, music, and more, each its own page.
            </li>
            <li>
              <strong>
                <Link to="/font-generator" className="text-accent hover:underline">Font generator</Link>
              </strong>{" "}
              — {fonts} text styles: bold, italic, cursive, fraktur, bubble, plus upside-down and glitchy zalgo text.
            </li>
            <li>
              <strong>
                <Link to="/kaomoji" className="text-accent hover:underline">Kaomoji &amp; text faces</Link>
              </strong>{" "}
              — {kaomoji} faces across {kaomojiMoods} moods, plus the dedicated{" "}
              <Link to="/lenny-faces" className="text-accent hover:underline">Lenny Face</Link>{" "}
              page ({lenny} faces). Add your own text to any of them.
            </li>
            <li>
              <strong>
                <Link to="/decorative" className="text-accent hover:underline">Decorative combos</Link>
              </strong>{" "}
              — {decoCombos} ready-made borders, dividers, loading bars and aesthetic flourishes across {decoCollections} collections.
            </li>
            <li>
              <strong>
                <Link to="/draw" className="text-accent hover:underline">Draw to Find</Link>
              </strong>{" "}
              — sketch a shape and we surface the closest matching symbols.
            </li>
            <li>
              <strong>
                <Link to="/export" className="text-accent hover:underline">Image export &amp; composer</Link>
              </strong>{" "}
              — turn any symbol into an SVG/PNG: add a styled caption, a circle/rounded background for an app icon
              or avatar, or repeat it into a divider, grid or border — then download, copy the image, or share a link.
            </li>
          </ul>

          <h2 className="font-display text-xl font-semibold">On every symbol page</h2>
          <p>
            Each symbol has its own page with the things you actually need: one-click copy,
            its Unicode codepoint(s) and name, the HTML entity, "copy as" formats, look-alikes
            and related characters — and the same SVG/PNG export. Found one you love? Add it to{" "}
            <Link to="/my-symbols" className="text-accent hover:underline">your favorites</Link>{" "}
            or a sharable{" "}
            <Link to="/pack" className="text-accent hover:underline">symbol pack</Link>; both live
            in your browser, not on a server.
          </p>

          <h2 className="font-display text-xl font-semibold">Built for speed and privacy</h2>
          <p>
            Every page is server-rendered to its own indexable URL with a unique title and
            structured data, then loads instantly. There are <strong>no ads, no popups and no
            tracking scripts</strong> — copying is genuinely one click, with no modal or
            confirmation in the way.
          </p>

          <h2 className="font-display text-xl font-semibold">Free and universal</h2>
          <p>
            Everything here is free for personal and commercial use, no attribution required.
            The symbols are part of the Unicode standard, so they paste and display anywhere —
            social bios, documents, code, design tools and chat apps — with no images to load
            and no fonts to install.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Browse all categories →
          </Link>
          <Link
            to="/font-generator"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface/60 px-5 py-2.5 text-sm font-semibold hover:bg-secondary"
          >
            Try the font generator
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
